import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { z } from "zod"
import {
  MATERIAL_SETUPS,
  type MaterialRule,
  type MaterialRuleStep,
} from "@/features/bill-of-materials/core/materialRules"
import { materialRulesQueryOptions } from "@/features/bill-of-materials/hooks/useMaterialRules"
import { shelfCodesQueryOptions } from "@/features/bill-of-materials/hooks/useShelfCodes"
import { deleteMaterialRuleFn } from "@/features/bill-of-materials/server/materialRules"
import { FillInWorkbook } from "@/features/list-of-materials/components/fill-in/FillInWorkbook"
import { ConfirmDialog } from "@/features/list-of-materials/components/form/ConfirmDialog"
import { HowLink } from "@/features/list-of-materials/components/how-it-works/HowLink"
import { ByShelfCodeView } from "@/features/list-of-materials/components/rules/ByShelfCodeView"
import { FillInView } from "@/features/list-of-materials/components/rules/FillInView"
import {
  RuleFormPanel,
  type RuleFormTarget,
} from "@/features/list-of-materials/components/rules/RuleFormPanel"
import { RuleList } from "@/features/list-of-materials/components/rules/RuleList"
import { RuleSentence } from "@/features/list-of-materials/components/rules/RuleSentence"
import { Button } from "@/features/list-of-materials/components/ui/Button"
import { Chip } from "@/features/list-of-materials/components/ui/Chip"
import { PageHead, PageStatus } from "@/features/list-of-materials/components/ui/PageHead"
import { TabLink, Tabs } from "@/features/list-of-materials/components/ui/Tabs"
import { setupProgress } from "@/features/list-of-materials/core/fillIn"
import { toShelfCodeSummary } from "@/features/list-of-materials/core/shelfCodeRows"
import { useCanEditLom } from "@/features/list-of-materials/hooks/useCanEditLom"
import {
  pickableArticlesQueryOptions,
  lomCodingQueryOptions,
  lomCountriesQueryOptions,
  lomStoreSizesQueryOptions,
} from "@/features/list-of-materials/hooks/useLomData"
import { useLomMutation } from "@/features/list-of-materials/hooks/useLomMutation"
import {
  lomVersionsQueryOptions,
  useSelectedLomVersion,
} from "@/features/list-of-materials/hooks/useLomVersion"

const VIEWS = ["rules", "fill-in", "by-code"] as const

export const Route = createFileRoute("/(authenticated)/list-of-materials/rules")({
  validateSearch: z.object({
    view: z.enum(VIEWS).optional().catch(undefined),
    code: z.union([z.string(), z.number()]).transform(String).optional(),
  }),
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: async ({ context, deps }) => {
    const { selected } = await context.queryClient.ensureQueryData(
      lomVersionsQueryOptions(deps.version),
    )
    if (!selected) return
    await Promise.all([
      context.queryClient.ensureQueryData(materialRulesQueryOptions(selected.id)),
      context.queryClient.ensureQueryData(shelfCodesQueryOptions(selected.id)),
      context.queryClient.ensureQueryData(pickableArticlesQueryOptions(selected.id)),
    ])
  },
  component: RulesPage,
})

const STEP_SETUPS: Record<MaterialRuleStep, MaterialRule["setups"]> = {
  base: [],
  smallBottomShelf: ["smallBottomShelf"],
  lowWall: ["lowWall"],
  w125: ["w125"],
}

function RulesPage() {
  const { version, versionSearch } = useSelectedLomVersion()
  const { view = "rules", code } = Route.useSearch()
  const canEdit = useCanEditLom()
  const { data: rules } = useSuspenseQuery(materialRulesQueryOptions(version.id))
  const { data: shelfCodes } = useSuspenseQuery(shelfCodesQueryOptions(version.id))
  const { data: articles } = useSuspenseQuery(pickableArticlesQueryOptions(version.id))
  const { data: coding } = useQuery(lomCodingQueryOptions(version.id))
  const { data: countries = [] } = useQuery(lomCountriesQueryOptions)
  const { data: storeSizes = [] } = useQuery(lomStoreSizesQueryOptions)
  const [target, setTarget] = useState<RuleFormTarget | null>(null)
  const [deleting, setDeleting] = useState<MaterialRule | null>(null)

  const describe = (nr: string) => articles.find((a) => a.articleNumber === nr)?.descriptions[0]
  const countryName = (id: string) => countries.find((c) => c.id === id)?.name
  const progress = useMemo(() => {
    const summaries = shelfCodes.map(toShelfCodeSummary)
    return MATERIAL_SETUPS.map((setup) => setupProgress(summaries, rules, setup))
  }, [shelfCodes, rules])
  const open = progress.reduce((sum, p) => sum + p.open.length, 0)

  const remove = useLomMutation(
    (rule: MaterialRule) => deleteMaterialRuleFn({ data: { id: rule.id } }),
    {
      success: t`Deleted the rule`,
      onSuccess: () => setDeleting(null),
    },
  )

  const tab = (value: (typeof VIEWS)[number], label: string, count?: number) => (
    <TabLink
      to="/list-of-materials/rules"
      search={{ ...versionSearch, view: value }}
      current={view === value}
      count={count}
    >
      {label}
    </TabLink>
  )

  return (
    <>
      <PageHead
        help={<HowLink topic="rules" />}
        title={<Trans>Rules</Trans>}
        sub={
          <Trans>
            What changes on a meter with a setup. Rules run top to bottom: every meter, Small Bottom
            Shelf, Low Wall, then 1.25 m.
          </Trans>
        }
        right={
          <PageStatus>
            {open > 0 ? (
              <Chip tone="fill" dot>
                {t`${open} to fill in`}
              </Chip>
            ) : (
              <Chip tone="ok" dot>
                <Trans>Every part decided</Trans>
              </Chip>
            )}
            {canEdit && (
              <Button tone="primary" onClick={() => setTarget({})}>
                <Trans>New rule</Trans>
              </Button>
            )}
          </PageStatus>
        }
      />
      <Tabs>
        {tab("rules", t`Rules`, rules.length)}
        {tab("fill-in", t`To fill in`, open)}
        {tab("by-code", t`By shelf code`)}
      </Tabs>

      {view === "rules" && (
        <RuleList
          rules={rules}
          describe={describe}
          countryName={countryName}
          onEdit={canEdit ? (rule) => setTarget({ rule }) : undefined}
          onDelete={canEdit ? setDeleting : undefined}
          onAdd={
            canEdit
              ? (step) => setTarget({ defaults: { setups: [...STEP_SETUPS[step]] } })
              : undefined
          }
        />
      )}
      {view === "fill-in" && <FillInWorkbook configVersionId={version.id} canEdit={canEdit} />}
      {view === "fill-in" && (
        <FillInView
          progress={progress}
          articles={articles}
          rules={rules}
          configVersionId={version.id}
          canEdit={canEdit}
          onAddCremeOnly={() => setTarget({ defaults: { action: "add", storeVersion: "1.0" } })}
          onEditRule={(rule) => setTarget({ rule })}
        />
      )}
      {view === "by-code" && (
        <ByShelfCodeView
          codes={shelfCodes}
          selectedCode={code}
          rules={rules}
          describe={describe}
          configVersionId={version.id}
          version={versionSearch.version}
          canEdit={canEdit}
        />
      )}

      <RuleFormPanel
        target={target}
        onClose={() => setTarget(null)}
        configVersionId={version.id}
        rules={rules}
        articles={articles}
        shelfCodes={shelfCodes.map((c) => c.code)}
        groups={(coding?.groups ?? []).map((g) => g.code)}
        countries={countries}
        storeSizes={storeSizes}
      />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={t`Delete this rule?`}
        confirmLabel={t`Delete rule`}
        pending={remove.isPending}
        error={remove.error}
        onConfirm={() => deleting && remove.mutate(deleting)}
      >
        {deleting && <RuleSentence rule={deleting} describe={describe} countryName={countryName} />}
      </ConfirmDialog>
    </>
  )
}
